import { useState, useRef } from "react";
import { RecaptchaWidget } from "@/components/ui/RecaptchaWidget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/lib/language";
import { useGeneralContactForm } from "@/lib/hooks";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, Send } from "lucide-react";

interface GeneralContactForm {
  name: string;
  email: string;
  phone: string;
  message: string;
  captchaToken: string;
}

export function LeadContactForm() {
  const { t, language, dir } = useLanguage();
  const [formData, setFormData] = useState<GeneralContactForm>({
    name: "",
    email: "",
    phone: "",
    message: "",
    captchaToken: "",
  });
  const recaptchaRef = useRef<{ reset: () => void }>(null);

  const contactMutation = useGeneralContactForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await contactMutation.mutateAsync(formData);
      toast.success(t("contactSuccess"));
      recaptchaRef.current?.reset();
      setFormData({ name: "", email: "", phone: "", message: "", captchaToken: "" });
    } catch (error) {
      toast.error(t("contactError"));
    }
  };

  return (
    <section id="contact" className="py-12 md:py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("getInTouch")}
            </h2>
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t("contactFormDescription")}
            </p>
          </div>

          {/* Contact Form Card */}
          <Card className="shadow-lg border-border" dir={dir}>
            <CardHeader className="pb-6">
              <CardTitle className="text-xl md:text-2xl">
                {t("sendMessage")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name Field */}
                  <div>
                    <Label htmlFor="name" className="text-base">
                      {t("name")} *
                    </Label>
                    <div className="h-2" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder={t("namePlaceholder")}
                      required
                      className="h-11"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <Label htmlFor="email" className="text-base">
                      <Mail className="inline w-4 h-4 mr-1" aria-hidden="true" />
                      {t("email")} *
                    </Label>
                    <div className="h-2" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder={t("emailPlaceholder")}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                {/* Phone Field */}
                <div>
                  <Label htmlFor="phone" className="text-base">
                    <Phone className="inline w-4 h-4 mr-1" aria-hidden="true" />
                    {t("phone")}
                  </Label>
                  <div className="h-2" />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder={t("phonePlaceholder")}
                    className="h-11"
                  />
                </div>

                {/* Message Field */}
                <div>
                  <Label htmlFor="message" className="text-base">
                    <MessageSquare className="inline w-4 h-4 mr-1" aria-hidden="true" />
                    {t("message")} *
                  </Label>
                  <div className="h-2" />
                  <Textarea
                    id="message"
                    rows={6}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    placeholder={t("messagePlaceholder")}
                    required
                    className="resize-none"
                  />
                </div>

                {/* reCAPTCHA */}
                <div>
                  <RecaptchaWidget
                    hl={language}
                    onChange={(token) =>
                      setFormData({ ...formData, captchaToken: token })
                    }
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={contactMutation.isPending || !formData.captchaToken}
                    size="lg"
                    className="w-full md:w-auto bg-accent hover:bg-accent/90 min-w-[200px]"
                  >
                    {contactMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t("sending")}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t("sendMessage")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">{t("phone")}</h3>
              <a
                href="tel:+972502364585"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                +972-50-236-4585
              </a>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">{t("email")}</h3>
              <a
                href="mailto:samantha@realestate-in-israel.com"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                samantha@realestate-in-israel.com
              </a>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">{t("whatsapp")}</h3>
              <a
                href="https://wa.me/972502364585"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
